<template>
  <el-col :span="24" id="currencies">
    <el-row type="flex" justify="start" align="middle" id="currencies-header">
      <HeaderInfoText :colspan="9" title="CURRENCIES" :meta="meta" />
      <el-col :span="6">
        <el-row type="flex" justify="center" v-if="meta.count > 20">
          <el-pagination
            layout="prev, pager, next, jumper"
            :total="meta.count"
            :page-size="20"
            :current-page.sync="currentPage">
          </el-pagination>
        </el-row>
      </el-col>
      <el-col :span="9" class="h-padded right-aligned-col">
        <el-button type="primary" icon="plus" @click="createNewModel" />
      </el-col>
    </el-row>
    <el-row type="flex" justify="end" align="middle">
      <el-col :span="24">
        <el-table
          v-if="meta.count && !changingPage"
          id="currencies-table"
          :data="pageData"
          border
          style="width: 100%; z-index: 0;"
          :maxHeight="tableHeight"
          :ref="`${endpoint}Table`"
          :row-class-name="tableRowClassName">
          <el-table-column prop="code" label="Code" align="center" width="120">
            <template slot-scope="scope">
              <el-row type="flex" justify="center">
                <a class="link-mock" @click="editExistingModel(scope.row.id)">
                  {{ scope.row.code }}
                </a>
              </el-row>
            </template>
          </el-table-column>
          <el-table-column prop="main_unit_name" label="Name" align="left" fit>
            <template slot-scope="scope">
              <el-row type="flex">
                <a class="link-mock" @click="editExistingModel(scope.row.id)">
                  {{ scope.row.main_unit_name }}
                </a>
              </el-row>
            </template>
          </el-table-column>
          <el-table-column prop="rate_vs_usd" label="Rate" align="center" fit>
            <template slot-scope="scope">
              <el-row type="flex" justify="center">
                <a class="link-mock" @click="editExistingModel(scope.row.id)">
                  {{ parseFloat(scope.row.rate_vs_usd).toFixed(4) }}
                </a>
              </el-row>
            </template>
          </el-table-column>
          <el-table-column prop="budgeted_rate" label="Budget Rate" align="center" fit>
            <template slot-scope="scope">
              <el-row type="flex" justify="center">
                <a class="link-mock" @click="editExistingModel(scope.row.id)">
                  {{ scope.row.budgeted_rate ? parseFloat(scope.row.budgeted_rate).toFixed(4) : "0.0000"}}
                </a>
              </el-row>
            </template>
          </el-table-column>
        </el-table>
      </el-col>
    </el-row>
    <el-dialog
      :title="editMode.active ? `Edit Currency` : 'Create New Currency'"
      size="small"
      :visible.sync="dialogFormVisible"
      :close-on-press-escape="false"
      :close-on-click-modal="false"
      :show-close="false">
      <NewEntityForm
        :modalOpen="dialogFormVisible"
        :fields="fields"
        :form.sync="form"
        :idPrefix="'a-'"
        ref="form"
        @formatDateFields="formatDateFields"
        @formChanged="handleFormChange"
      />
      <span slot="footer" class="dialog-footer">
        <el-button :disabled="saving || !!updating" @click="closeFormModal">Cancel</el-button>
        <el-button :loading="saving || !!updating" type="primary" @click="submitForm">Submit</el-button>
      </span>
    </el-dialog>
  </el-col>
</template>

<script>
  import { mapGetters } from 'vuex'
  import store from '@/store/store'
  import { payload } from '@/components/RouteComponents/Currencies/actionPayload'
  import NewEntityForm from '@/components/NewEntityForm.vue'
  import HeaderInfoText from '@/components/HeaderInfoText'
  import { formatDateFields } from '@/utils/helpers/FormatDate'
  import { createNewModel } from '@/components/RouteComponents/commonFunctions'
  import { systemViewMixins } from '@/Mixins/SystemViewMixins'
  import { DataLoader } from '@/utils/helpers/DataLoader/DataLoader'

  export default {
    components: {
      NewEntityForm,
      HeaderInfoText
    },
    mixins: [ systemViewMixins ],
    data () {
      return {
        tableHeight: 1000,
        showFilters: true,
        batchUpdateOptions: [
          { id: 0, text: 'Delete selected currencies' }
        ],
        filter: {
          type: [],
          searchCriteria: null
        },
        dialogFormVisible: false,
        changingPage: false,
        editMode: { active: false, modelId: null }
      }
    },
    methods: {
      /**
       * Resets editMode data, resets form to initial state, and displays the
       * modal to create a new model
       */
      createNewModel () {
        createNewModel(this)
      },
      /**
       * Handles changes to the form
       * @param targetField   The field to be changed
       */
      handleFormChange (targetField) {
        this.trackFormChange()
        if (targetField === 'country' && this.form.country && this.form.country.value) {
          const storeActionPayload = { entityType: 'countries', mutation: 'RECEIVE_COUNTRIES' }
          let dependenciesActionPayload = { countries: '' }
          let promises = []
          const { value } = this.form.country
          this.$store.dispatch('storeLastSearchResults', storeActionPayload)
            .then(() => {
              const model = this.getCountryById(value)
              if (model && Object.keys(model).length) {
                dependenciesActionPayload.countries = [model]
                promises.push(
                  this.$store.dispatch('findDependencies', dependenciesActionPayload)
                    .then(p => Promise.all(p))
                    .catch(e => console.log(e))
                )
              }
              return Promise.all(promises)
            })
            .catch(e => console.log(e))
        }
        if (targetField === 'is_base' && this.form.is_base) {
          const baseCurrency = this.data.find((c) => c.is_base)
          if (baseCurrency && (this.form.code !== baseCurrency.code)) {
            this.$notify({ title: 'Message', message: `Your base currency will be changed ${this.form.code ? 'to ' + this.form.code : ''} from ${baseCurrency.code}, Kindly update the rates for non base currencies once you save`, type: 'info' })
          }
        }
      },
      /**
       * Formats date fields
       */
      formatDateFields () {
        formatDateFields(this, 'rate_expires', false)
      }
    },
    computed: {
      ...mapGetters({
        getCountryById: 'getCountryById'
      }),
      data () {
        return this.sortData(this.$store.getters.getAllCurrencies)
      },
      pageData () {
        return this.meetsFilterCriteria(this.data).slice((this.currentPage - 1) * 20, this.currentPage * 20)
      },
      meta () {
        return { count: this.meetsFilterCriteria(this.data).length, shown: this.pageData.length }
      },
      fields () {
        const fields = this.$store.getters.getResourceFieldsByEndpoint(this.endpoint)
        // fields.rate_vs_usd.note_text = '*Designate the base currency by using a rate of 1.00.'
        fields.rate_vs_usd.decimals = 4
        fields.budgeted_rate.decimals = 4
        return (fields && typeof fields === 'object') ? Object.values(fields).filter(field => field.name !== 'rate_expires') : []
      }
    },
    /**
     * Pre-route enter logic.
     * @param to
     * @param from
     * @param next
     */
    async beforeRouteEnter (to, from, next) {
      let initPromises = []
      const fieldsAlreadyExist = typeof store.getters.getResourceFieldsByEndpoint(payload.endpoint) !== 'undefined'
      if (!fieldsAlreadyExist) {
        initPromises.push(store.dispatch('fetchOptions', payload))
      }
      try {
        await Promise.all(initPromises)
        if (from.path !== '/') {
          let dataLoader = new DataLoader(store, [ payload ], true, 200)
          await dataLoader.loadData()
        }
      } catch (e) {
        console.log(e)
      } finally {
        next()
      }
    },
    created () {
      this.endpoint = payload.endpoint
      this.moduleName = payload.moduleName
      this.payload = payload
      this.generateForm()
    },
    mounted () {
      this.$nextTick = () => {
        const tableHeight = () => {
          const parentHeight = document.getElementById('currencies').offsetHeight
          const headerHeight = document.getElementById('currencies-header').offsetHeight
          return parentHeight - headerHeight
        }
        this.tableHeight = tableHeight()
      }
    }
  }
</script>

<style scoped>
  #currencies-header {
    border-bottom: 1px solid lightgray;
  }
  .h-padded {
    padding: 0 12px;
  }
  .right-aligned-col {
    text-align: right;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
</style>



// WEBPACK FOOTER //
// src/components/RouteComponents/Currencies/Currencies.vue